import { apiFetch } from "../../utils/apiFetch";
import { buildAssetUrl } from "../../utils/api";

const SUBSCRIPTIONS_BASE = "api/v1/customer/subscriptions";

const asArray = (value) => (Array.isArray(value) ? value : []);

const CP1252_BYTE_OVERRIDES = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84,
  0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88,
  0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c,
  0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93,
  0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b,
  0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};

const repairMojibake = (value) => {
  if (typeof value !== "string" || !/(?:Ã.|Â.|â|à¤)/.test(value)) return value;

  try {
    const bytes = [];
    for (const character of value) {
      const codePoint = character.codePointAt(0);
      const byte = codePoint <= 0xff ? codePoint : CP1252_BYTE_OVERRIDES[codePoint];
      if (byte === undefined) return value;
      bytes.push(byte);
    }

    return new TextDecoder("utf-8", { fatal: true }).decode(new Uint8Array(bytes));
  } catch {
    return value;
  }
};

const normalizeProduct = (product = {}) => {
  const imageUrls = [
    product.image1,
    product.image2,
    product.image3,
    product.image4,
    product.image5,
    product.image6,
  ]
    .filter(Boolean)
    .map(buildAssetUrl);

  return {
    ...product,
    product_id: product.product_id ?? product.id,
    name: product.name ?? product.product_name ?? "Product",
    description: repairMojibake(product.description),
    image: imageUrls[0] || null,
    images: imageUrls,
    variants: asArray(product.variants).map((variant) => ({
      ...variant,
      product_variant_id:
        variant.product_variant_id ?? variant.id ?? variant.variant_id,
      variant_id: variant.variant_id ?? variant.product_variant_id ?? variant.id,
      variant_name: variant.variant_name ?? variant.name ?? "Default",
      unit_name: variant.unit_name ?? variant.unit ?? "",
      original_price: variant.original_price ?? variant.price ?? "0",
      discount_price:
        variant.discount_price ?? variant.sale_price ?? variant.price ?? "0",
    })),
  };
};

const normalizeSubscription = (subscription = {}) => ({
  ...subscription,
  subscription_id: subscription.subscription_id ?? subscription.id,
  product_name:
    subscription.product_name ?? subscription.name ?? subscription.product?.name,
  image1: buildAssetUrl(subscription.image1 || subscription.product?.image1),
  image2: buildAssetUrl(subscription.image2 || subscription.product?.image2),
  quantity: subscription.quantity
    ? (Number.isInteger(Number(subscription.quantity))
      ? Number(subscription.quantity)
      : Math.round(Number(subscription.quantity)))
    : 1,
  quantity_unit: subscription.quantity_unit ?? subscription.unit_name ?? "litre",
  frequency: subscription.frequency ?? "daily",
  plan_type: subscription.plan_type ?? "monthly",
  delivery_slot: subscription.delivery_slot ?? "06:00-08:00",
  payment_method: subscription.payment_method ?? "monthly_billing",
  status: subscription.status ?? "active",
});

const extractSubscriptionRecord = (payload = {}) => {
  let record = payload?.data ?? payload;
  if (record?.data) record = record.data;
  if (record?.subscription) record = record.subscription;
  if (record?.subscription_details) record = record.subscription_details;
  return record && typeof record === "object" && !Array.isArray(record)
    ? record
    : {};
};

const applySubscriptionResponse = (state, subscriptionId, payload) => {
  const existing =
    state.subscriptions.find(
      (item) => String(item.subscription_id) === String(subscriptionId),
    ) ||
    (String(state.subscription_detail?.subscription_id) === String(subscriptionId)
      ? state.subscription_detail
      : {});
  const record = extractSubscriptionRecord(payload);
  const updated = normalizeSubscription({
    ...existing,
    ...record,
    subscription_id: record.subscription_id ?? record.id ?? subscriptionId,
  });

  return {
    subscriptions: updateSubscriptionInList(state.subscriptions, updated),
    subscription_detail:
      String(state.subscription_detail?.subscription_id) === String(subscriptionId)
        ? { ...state.subscription_detail, ...updated }
        : state.subscription_detail,
  };
};

const normalizeDeliveryPage = (payload = {}) => {
  const data = payload.data ?? payload;
  return {
    total: Number(data.total ?? 0),
    page: Number(data.page ?? 1),
    limit: Number(data.limit ?? 20),
    deliveries: asArray(data.deliveries),
  };
};

const getMandateResponseSources = (payload = {}) =>
  [
    payload,
    payload.data,
    payload.subscription,
    payload.subscription_details,
    payload.cashfree,
    payload.cashfree?.subscription,
  ].filter((value) => value && typeof value === "object");

const extractPaymentLink = (payload = {}) => {
  const keys = [
    "payment_link",
    "paymentLink",
    "auth_link",
    "authLink",
    "link",
    "url",
  ];
  for (const source of getMandateResponseSources(payload)) {
    const link = keys.map((key) => source[key]).find(Boolean);
    if (link) return link;
  }
  return null;
};

const extractSubscriptionSessionId = (payload = {}) => {
  const keys = [
    "subscription_session_id",
    "subscriptionSessionId",
    "subsSessionId",
    "cashfree_subscription_session_id",
  ];
  for (const source of getMandateResponseSources(payload)) {
    const sessionId = keys.map((key) => source[key]).find(Boolean);
    if (sessionId) return sessionId;
  }
  return null;
};

// Default fallback values used if /config fails or hasn't loaded yet
const DEFAULT_CONFIG = {
  products: [],
  plans: [
    { plan_id: 1, plan_name: "Weekly Subscription", plan_key: "weekly", duration_days: 7 },
    { plan_id: 2, plan_name: "Monthly Subscription", plan_key: "monthly", duration_days: 30 },
  ],
  quantities: [
    { qty_id: 2, label: "1 Litre", value: "1.00", unit: "litre", is_custom: false },
    { qty_id: 3, label: "2 Litres", value: "2.00", unit: "litre", is_custom: false },
    { qty_id: 5, label: "3 Litres", value: "3.00", unit: "litre", is_custom: false },
  ],
  frequencies: [
    { freq_id: 1, label: "Daily", freq_key: "daily" },
    { freq_id: 2, label: "Alternate Day", freq_key: "alternate_day" },
    { freq_id: 3, label: "Weekly", freq_key: "weekly" },
  ],
  delivery_slots: [
    { slot_id: 1, label: "6 AM - 8 AM", slot_key: "06:00-08:00" },
    { slot_id: 2, label: "8 AM - 10 AM", slot_key: "08:00-10:00" },
  ],
  payment_methods: [
    { pm_id: 1, label: "UPI AutoPay", pm_key: "upi_autopay", is_recommended: true },
    { pm_id: 2, label: "Wallet", pm_key: "wallet", is_recommended: false },
    { pm_id: 3, label: "Monthly Billing", pm_key: "monthly_billing", is_recommended: false },
  ],
};

const updateSubscriptionInList = (subscriptions, updated) => {
  const normalized = normalizeSubscription(updated);
  return subscriptions.map((subscription) =>
    String(subscription.subscription_id) === String(normalized.subscription_id)
      ? { ...subscription, ...normalized }
      : subscription,
  );
};

export const createSubscriptionsSlice = (set, get) => ({
  // ── Config (STEP 0) ──────────────────────────────────────────────────────────
  subscription_config: DEFAULT_CONFIG,
  subscription_config_loading: false,
  subscription_config_error: null,

  // ── Products (derived from config, kept for compat) ──────────────────────────
  subscription_products: [],
  subscription_products_loading: false,
  subscription_products_error: null,

  // ── List ─────────────────────────────────────────────────────────────────────
  subscriptions: [],
  subscriptions_loading: false,
  subscriptions_error: null,

  // ── Detail ───────────────────────────────────────────────────────────────────
  subscription_detail: null,
  subscription_detail_loading: false,

  // ── Deliveries ───────────────────────────────────────────────────────────────
  subscription_deliveries: {
    total: 0,
    page: 1,
    limit: 20,
    deliveries: [],
  },
  subscription_deliveries_loading: false,

  // ── Mutation loading ─────────────────────────────────────────────────────────
  subscription_action_loading: false,

  // ── STEP 0: Fetch config + full product list ─────────────────────────────────
  fetchSubscriptionConfig: async () => {
    set({
      subscription_config_loading: true,
      subscription_products_loading: true,
      subscription_config_error: null,
      subscription_products_error: null,
    });

    try {
      // Fire both requests in parallel — config for plans/slots/etc., products for full list
      const [configJson, productsJson] = await Promise.allSettled([
        apiFetch(`${SUBSCRIPTIONS_BASE}/config`, { auth: false }),
        apiFetch(`${SUBSCRIPTIONS_BASE}/products`, { auth: false }),
      ]);

      // ── Config data ──────────────────────────────────────────────────────────
      const raw = configJson.status === "fulfilled" ? (configJson.value.data ?? {}) : {};
      const configProducts = asArray(raw.products).map(normalizeProduct);

      // ── Products data (may return more items than config) ────────────────────
      const extraProducts =
        productsJson.status === "fulfilled"
          ? asArray(productsJson.value.data).map(normalizeProduct)
          : [];

      // Merge: config products first (they have the most complete data),
      // then append any extra products not already present in the config list
      const configIds = new Set(configProducts.map((p) => String(p.product_id)));
      const mergedProducts = [
        ...configProducts,
        ...extraProducts.filter((p) => !configIds.has(String(p.product_id))),
      ];

      const config = {
        products: mergedProducts,
        plans: asArray(raw.plans).length ? raw.plans : DEFAULT_CONFIG.plans,
        quantities: asArray(raw.quantities).length ? raw.quantities : DEFAULT_CONFIG.quantities,
        frequencies: asArray(raw.frequencies).length ? raw.frequencies : DEFAULT_CONFIG.frequencies,
        delivery_slots: asArray(raw.delivery_slots).length ? raw.delivery_slots : DEFAULT_CONFIG.delivery_slots,
        payment_methods: asArray(raw.payment_methods).length ? raw.payment_methods : DEFAULT_CONFIG.payment_methods,
      };

      set({
        subscription_config: config,
        subscription_products: mergedProducts,
        subscription_config_loading: false,
        subscription_products_loading: false,
      });

      return config;
    } catch (error) {
      set({
        subscription_config_error: error.message,
        subscription_products_error: error.message,
        subscription_config_loading: false,
        subscription_products_loading: false,
      });
      return DEFAULT_CONFIG;
    }
  },

  // ── API 1: Create / Activate ─────────────────────────────────────────────────
  createSubscription: async (payload) => {
    set({ subscription_action_loading: true, subscriptions_error: null });

    try {
      const json = await apiFetch(SUBSCRIPTIONS_BASE, {
        method: "POST",
        body: {
          product_id: Number(payload.product_id),
          product_variant_id: Number(payload.product_variant_id),
          address_id: Number(payload.address_id),
          frequency: payload.frequency,
          plan_type: payload.plan_type,
          ...(payload.frequency === "twice_a_day"
            ? {
                morning_slot: payload.morning_slot,
                morning_quantity: Number(payload.morning_quantity),
                evening_slot: payload.evening_slot,
                evening_quantity: Number(payload.evening_quantity),
              }
            : {
                quantity: Number(payload.quantity),
                quantity_unit: payload.quantity_unit || "litre",
                delivery_slot: payload.delivery_slot,
              }),
          payment_method: payload.payment_method,
          ...(payload.start_date ? { start_date: payload.start_date } : {}),
          ...(payload.payment_method === "upi_autopay"
            ? {
                subscription_meta: {
                  return_url: `${window.location.origin}/subscription/mandate/return`,
                },
              }
            : {}),
        },
      });

      const subscription = normalizeSubscription(extractSubscriptionRecord(json));
      set((state) => ({
        subscriptions: [subscription, ...state.subscriptions],
        subscription_detail: subscription,
        subscription_action_loading: false,
      }));
      // Return full response so the caller can access cashfree.subscription_session_id / payment_link
      return { subscription, raw: json.data };
    } catch (error) {
      set({
        subscriptions_error: error.message,
        subscription_action_loading: false,
      });
      throw error;
    }
  },

  // ── UPI AutoPay: Poll until mandate resolves ─────────────────────────────────
  // Resolves the hosted checkout data returned by the subscription API.
  getSubscriptionMandateCheckout: (subscriptionData) => {
    const existingLink = extractPaymentLink(subscriptionData);
    if (existingLink) return { paymentLink: existingLink };

    const subscriptionSessionId =
      extractSubscriptionSessionId(subscriptionData);
    if (subscriptionSessionId) return { subscriptionSessionId };

    throw new Error(
      "The subscription was created, but the API response did not include a Cashfree subscription_session_id or payment_link.",
    );
  },

  syncSubscriptionStatus: async (subscriptionId) => {
    set({ subscription_action_loading: true, subscriptions_error: null });
    try {
      const json = await apiFetch(
        `${SUBSCRIPTIONS_BASE}/${encodeURIComponent(subscriptionId)}/sync-status`,
        { method: "POST" },
      );
      set({ subscription_action_loading: false });
      return json.data ?? json;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  sendSubscriptionModifyOtp: async (subscriptionId) => {
    set({ subscription_action_loading: true, subscriptions_error: null });
    try {
      const json = await apiFetch(
        `${SUBSCRIPTIONS_BASE}/${encodeURIComponent(subscriptionId)}/send-modify-otp`,
        { method: "POST" },
      );
      set({ subscription_action_loading: false });
      return json.data ?? json;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // Polls GET /subscriptions/:id every 2.5 s for up to 15 s.
  // Returns the final status string: 'active' | 'pending_mandate' | 'cancelled' | 'failed' | ...
  verifySubscriptionStatus: async (subscriptionId) => {
    const MAX_ATTEMPTS = 6;
    const INTERVAL_MS = 2500;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      try {
        const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}`);
        const status = (json.data?.status ?? "pending_mandate").toLowerCase();
        const subscription = normalizeSubscription(json.data);

        // Keep store in sync on every poll
        set((state) => ({
          subscription_detail: subscription,
          subscriptions: updateSubscriptionInList(state.subscriptions, subscription),
        }));

        // Stop polling once mandate is no longer pending
        if (status !== "pending_mandate") return status;
      } catch {
        // Network blip — keep trying
      }
    }

    // Timed out — mandate still pending (Cashfree processes async)
    return "pending_mandate";
  },

  // ── API 2: List ──────────────────────────────────────────────────────────────
  fetchSubscriptions: async (status = "") => {
    set({ subscriptions_loading: true, subscriptions_error: null });

    try {
      const query = status ? `?status=${encodeURIComponent(status)}` : "";
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}${query}`);
      const subscriptions = asArray(json.data).map(normalizeSubscription);
      set({ subscriptions, subscriptions_loading: false });
      return subscriptions;
    } catch (error) {
      set({ subscriptions_error: error.message, subscriptions_loading: false });
      return [];
    }
  },

  // ── API 3: Detail ────────────────────────────────────────────────────────────
  fetchSubscriptionDetail: async (subscriptionId) => {
    set({ subscription_detail_loading: true, subscriptions_error: null });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}`);
      const subscription = normalizeSubscription(extractSubscriptionRecord(json));
      set({
        subscription_detail: subscription,
        subscription_detail_loading: false,
      });
      return subscription;
    } catch (error) {
      set({
        subscriptions_error: error.message,
        subscription_detail_loading: false,
      });
      throw error;
    }
  },

  // ── API 4: Pause ─────────────────────────────────────────────────────────────
  pauseSubscription: async (subscriptionId, payload) => {
    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/pause`, {
        method: "POST",
        body: {
          pause_from: payload.pause_from,
          pause_to: payload.pause_to,
          reason: payload.reason || "temporary_pause",
          ...(payload.comments?.trim()
            ? { comments: payload.comments.trim() }
            : {}),
        },
      });

      await get().fetchSubscriptionDetail(subscriptionId);
      await get().fetchSubscriptions();
      set({ subscription_action_loading: false });
      return json.data;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 5: Resume ────────────────────────────────────────────────────────────
  resumeSubscription: async (subscriptionId, resumeDate = "") => {
    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/resume`, {
        method: "POST",
        body: resumeDate ? { resume_from: resumeDate } : {},
      });

      await get().fetchSubscriptionDetail(subscriptionId);
      await get().fetchSubscriptions();
      set({ subscription_action_loading: false });
      return json.data;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 6: Change Quantity ───────────────────────────────────────────────────
  changeSubscriptionQuantity: async (subscriptionId, payload) => {
    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/quantity`, {
        method: "PATCH",
        body: {
          quantity: Number(payload.quantity),
          otp: String(payload.otp || "").trim(),
        },
      });

      let updated;
      set((state) => {
        const next = applySubscriptionResponse(state, subscriptionId, json);
        updated = next.subscriptions.find(
          (item) => String(item.subscription_id) === String(subscriptionId),
        ) || next.subscription_detail;
        return { ...next, subscription_action_loading: false };
      });
      return updated;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 7: Change Plan / Frequency / Slot ───────────────────────────────────
  changeSubscriptionPlan: async (subscriptionId, payload) => {
    const body = {};
    if (payload.plan_type) body.plan_type = payload.plan_type;
    body.otp = String(payload.otp || "").trim();

    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/plan`, {
        method: "PATCH",
        body,
      });

      let updated;
      set((state) => {
        const next = applySubscriptionResponse(state, subscriptionId, json);
        updated = next.subscriptions.find(
          (item) => String(item.subscription_id) === String(subscriptionId),
        ) || next.subscription_detail;
        return { ...next, subscription_action_loading: false };
      });
      return updated;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 8: Delivery History ──────────────────────────────────────────────────
  fetchSubscriptionDeliveries: async (
    subscriptionId,
    { page = 1, limit = 20 } = {},
  ) => {
    set({ subscription_deliveries_loading: true });

    try {
      const json = await apiFetch(
        `${SUBSCRIPTIONS_BASE}/${subscriptionId}/deliveries?page=${page}&limit=${limit}`,
      );
      const deliveryPage = normalizeDeliveryPage(json);
      set({
        subscription_deliveries: deliveryPage,
        subscription_deliveries_loading: false,
      });
      return deliveryPage;
    } catch (error) {
      set({
        subscriptions_error: error.message,
        subscription_deliveries_loading: false,
      });
      throw error;
    }
  },

  // ── API 9: Cancel ────────────────────────────────────────────────────────────
  cancelSubscription: async (subscriptionId) => {
    set({ subscription_action_loading: true });

    try {
      await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}`, {
        method: "DELETE",
      });

      set((state) => ({
        subscriptions: state.subscriptions.map((subscription) =>
          String(subscription.subscription_id ?? subscription.id) === String(subscriptionId)
            ? { ...subscription, status: "cancelled" }
            : subscription,
        ),
        subscription_detail:
          String(state.subscription_detail?.subscription_id ?? state.subscription_detail?.id) ===
          String(subscriptionId)
            ? { ...state.subscription_detail, status: "cancelled" }
            : state.subscription_detail,
        subscription_action_loading: false,
      }));
      return true;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },
});
